import formbricks from "@formbricks/js";
import type { ProfilesData } from "../store/useAuthStore";

export const formbricksAppUrl =
  import.meta.env.VITE_FORMBRICKS_APP_URL?.trim() ||
  "https://app.formbricks.com";

export const formbricksEnvironmentId =
  import.meta.env.VITE_FORMBRICKS_ENVIRONMENT_ID?.trim() ||
  "cmpa2sbfazmyxu901r8ot1wtq";

export const isFormbricksEnabled =
  import.meta.env.VITE_ENABLE_FORMBRICKS !== "false" &&
  Boolean(formbricksEnvironmentId);

export const isFormbricksUserIdentificationEnabled =
  import.meta.env.VITE_FORMBRICKS_USER_IDENTIFICATION === "true";

let initialized = false;
let initPromise: Promise<void> | null = null;

function waitUntilAfterPageLoad(): Promise<void> {
  // In dev/localhost, init immediately so surveys are testable without waiting.
  if (import.meta.env.DEV) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const schedule = () => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => resolve(), { timeout: 4000 });
      } else {
        setTimeout(resolve, 4000);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
  });
}

export async function initFormbricks(): Promise<void> {
  if (!isFormbricksEnabled || initialized || typeof window === "undefined") {
    return;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    try {
      // Defer setup until after load + idle so Formbricks' survey preload
      // does not trigger "preloaded but not used" warnings on initial paint.
      await waitUntilAfterPageLoad();
      if (initialized) {
        return;
      }

      await formbricks.setup({
        environmentId: formbricksEnvironmentId,
        appUrl: formbricksAppUrl,
      });
      initialized = true;
      await formbricks.registerRouteChange();
    } catch (error) {
      console.warn("[Formbricks] setup failed:", error);
    }
  })();

  return initPromise;
}

function getUnifiedAccountId(profilesData: ProfilesData | null): string | null {
  const unifiedAccountId =
    profilesData?.unified_account_details?.unified_account_id;
  return unifiedAccountId ? String(unifiedAccountId) : null;
}

function buildFormbricksAttributes(
  profilesData: ProfilesData | null,
): Record<string, string> {
  const profiles = profilesData?.unified_account_details?.profiles;
  const defaultProfile =
    profiles?.find((p) => p.is_default_profile) ?? profiles?.[0];
  const uad = profilesData?.unified_account_details?.user_account_details;
  const attributes: Record<string, string> = {
    platform: "web",
    environment: import.meta.env.MODE,
  };

  if (defaultProfile?.uam_id != null) {
    attributes.uam_id = String(defaultProfile.uam_id);
  }
  if (defaultProfile?.vertical_id != null) {
    attributes.vertical_id = String(defaultProfile.vertical_id);
  }
  if (defaultProfile?.tenant_id != null) {
    attributes.tenant_id = String(defaultProfile.tenant_id);
  }
  if (uad?.grade?.gradeId != null) {
    attributes.grade_id = String(uad.grade.gradeId);
  }
  if (uad?.email) {
    attributes.email = uad.email;
  }
  if (uad?.phone) {
    attributes.phone = uad.phone;
  }

  return attributes;
}

export async function syncFormbricksUserIdentity(
  profilesData: ProfilesData | null,
): Promise<void> {
  if (!isFormbricksEnabled || !isFormbricksUserIdentificationEnabled) {
    return;
  }

  await initFormbricks();
  if (!initialized) {
    return;
  }

  const userId = getUnifiedAccountId(profilesData);
  if (!userId) {
    return;
  }

  try {
    await formbricks.setUserId(userId);
    const attributes = buildFormbricksAttributes(profilesData);
    if (Object.keys(attributes).length > 0) {
      await formbricks.setAttributes(attributes);
    }
  } catch (error) {
    console.warn("[Formbricks] identity sync failed:", error);
  }
}

export async function clearFormbricksIdentity(): Promise<void> {
  if (
    !isFormbricksEnabled ||
    !isFormbricksUserIdentificationEnabled ||
    !initialized
  ) {
    return;
  }

  try {
    await formbricks.logout();
  } catch (error) {
    console.warn("[Formbricks] logout failed:", error);
  }
}

export async function registerFormbricksRouteChange(): Promise<void> {
  if (!isFormbricksEnabled) {
    return;
  }

  await initFormbricks();
  if (!initialized) {
    return;
  }

  try {
    await formbricks.registerRouteChange();
  } catch (error) {
    console.warn("[Formbricks] route change failed:", error);
  }
}
